import React from 'react'
import {
  DataQualityAssessmentResponse,
  DGDashboardAPIResponse,
  ListDataAssetsAPIResponse,
} from '@/app/data-catalog/dashboard/types'
import { useApiRequest } from '@/lib/http-utils'
import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { taskManagerApiPaths } from './_paths'

type DGDashboardQueryTypes = {
  filters: { [key: string]: string }
  queryOptions?: Omit<UseQueryOptions<DGDashboardAPIResponse>, 'queryFn' | 'queryKey'>
}

type DataQualityAssessmentCardsType = {
  filters: { [key: string]: string }
  queryOptions?: Omit<UseQueryOptions<DataQualityAssessmentResponse>, 'queryFn' | 'queryKey'>
}

type DataAssetsQueryTypes = {
  queryOptions?: Omit<UseQueryOptions<ListDataAssetsAPIResponse>, 'queryFn' | 'queryKey'>
}

export function useMetaDataScoreCardsQuery({ filters, queryOptions = {} }: DGDashboardQueryTypes) {
  const { apiRequest, domainId } = useApiRequest()
  const agentId = 'em-data-catalog-agent'

  const jsonBody = React.useMemo(() => {
    const database = filters.database
    const schema = filters.schema
    const table = filters.table
    const sensitivity = filters.sensitivity

    // --- Build entity_id dynamically ---
    const entity_id: Record<string, string> = {}

    if (database && database !== 'all') {
      entity_id.database_name = database
    }

    if (schema && schema !== 'all') {
      entity_id.schema_name = schema
    }

    if (table && table !== 'all') {
      entity_id.table_name = table
    }

    // --- Derive scope ---
    let scope: string = 'service'
    if (table && table !== 'all') {
      scope = 'table'
    } else if (schema && schema !== 'all') {
      scope = 'databaseSchema'
    } else if (database && database !== 'all') {
      scope = 'database'
    }

    // --- Build payload ---
    const payload = {
      computeScorecardInput: {
        entity_id:
          Object.keys(entity_id).length > 0
            ? { service_name: 'em-data-governance', ...entity_id }
            : { service_name: 'em-data-governance' },
        scope,
        sensitivity_filter: sensitivity && sensitivity !== 'all' ? sensitivity : undefined,
      },
    }

    // --- Clean undefined fields ---
    return JSON.parse(JSON.stringify(payload))
  }, [filters])

  return useQuery({
    queryKey: ['dg-dashboard-metadataView', JSON.stringify(jsonBody)],
    queryFn: async () => {
      const basePath = taskManagerApiPaths.agentById({ domainId, agentId })
      const response = await apiRequest({
        path: basePath,
        queryParams: {
          tool_id: 'computeOverallScorecard',
        },
        options: {
          method: 'POST',
          body: JSON.stringify(jsonBody),
        },
      })

      if (!response.ok) {
        throw new Error('Error fetching metadata score card')
      }
      const json = (await response.json()) as { output: DGDashboardAPIResponse }
      return json.output
    },
    ...queryOptions,
  })
}

export function useDataQualScoreCardsQuery({
  filters,
  queryOptions = {},
}: DataQualityAssessmentCardsType) {
  const { apiRequest, domainId } = useApiRequest()
  const agentId = 'em-data-catalog-agent'

  const jsonBody = React.useMemo(() => {
    const database = filters.database
    const schema = filters.schema
    const table = filters.table
    const sensitivity = filters.sensitivity
    // --- Build payload ---
    const payload = {
      dataQualityScorecardInput: {
        service_name: 'em-data-governance',
        database_name: database && database !== 'all' ? database : undefined,
        schema_name: schema && schema !== 'all' ? schema : undefined,
        table_name: table && table !== 'all' ? table : undefined,
        sensitivity_levels: sensitivity && sensitivity !== 'all' ? [sensitivity] : undefined,
      },
    }

    // --- Clean undefined fields ---
    return JSON.parse(JSON.stringify(payload))
  }, [filters])

  return useQuery({
    queryKey: ['dg-dashboard-dataQuality', JSON.stringify(jsonBody)],
    queryFn: async () => {
      const basePath = taskManagerApiPaths.agentById({ domainId, agentId })
      const response = await apiRequest({
        path: basePath,
        queryParams: {
          tool_id: 'computeDataQualityScorecard',
        },
        options: {
          method: 'POST',
          body: JSON.stringify(jsonBody),
        },
      })

      if (!response.ok) {
        throw new Error('Error fetching metadata score card')
      }
      const json = (await response.json()) as { output: DataQualityAssessmentResponse }
      return json.output
    },
    ...queryOptions,
  })
}

export function useListDataAssetsQuery({ queryOptions }: DataAssetsQueryTypes) {
  const { apiRequest, domainId } = useApiRequest()
  const agentId = 'em-data-catalog-agent'
  return useQuery({
    queryKey: ['dg-dashboard-data-assets', domainId],
    queryFn: async () => {
      const basePath = taskManagerApiPaths.agentById({ domainId, agentId })
      const response = await apiRequest({
        path: basePath,
        queryParams: {
          tool_id: 'listDataAssets',
        },
        options: {
          method: 'POST',
          body: JSON.stringify({
            list_data_assets_input: {
              database_service_name: 'em-data-governance',
            },
          }),
        },
      })

      if (!response.ok) {
        throw new Error('Error fetching metadata score card')
      }
      const json = (await response.json()) as { output: ListDataAssetsAPIResponse }

      return json.output
    },
    ...queryOptions,
  })
}
